import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { axios_tenant } from "../../../axios";
import finalOutputTemplate from "./constants/DV_Final_Output.xlsx";

const PROPERTY_TYPES = ["Patent", "Trademark"];

const VERIFICATION_TYPE_OPTIONS = {
  Patent: [
    "Bibliographic Data",
    "Legal Status",
    "Family Data",
  ],
  Trademark: [
    "Bibliographic Data",
    "Owner Details",
    "Related Cases",

  ],
};

const VERIFICATION_SOURCE_OPTIONS = {
  Patent: ["USPTO", "EPO", "WIPO", "Google Patents"],
  Trademark: ["USPTO", "EUIPO", "IPAU","WIPO", "Google Patents"],
};

const DISABLED_PROPERTY_TYPES = new Set(["Patent"]);

const DISABLED_VERIFICATION_TYPES = {
  Patent: new Set(["Legal Status", "Family Data"]),
  Trademark: new Set(["Related Cases"]),
};

const DISABLED_VERIFICATION_SOURCES = {
  Patent: new Set(["EPO", "WIPO", "Google Patents"]),
  Trademark: new Set(["WIPO", "Google Patents"]),
};

const getEnabledVerificationTypes = (propertyType) =>
  (VERIFICATION_TYPE_OPTIONS[propertyType] || []).filter(
    (option) => !(DISABLED_VERIFICATION_TYPES[propertyType] || new Set()).has(option)
  );

const getEnabledVerificationSources = (propertyType) =>
  (VERIFICATION_SOURCE_OPTIONS[propertyType] || []).filter(
    (option) => !(DISABLED_VERIFICATION_SOURCES[propertyType] || new Set()).has(option)
  );

const selectMenuProps = {
  PaperProps: {
    sx: {
      mt: 1,
      borderRadius: "14px",
      border: "1px solid #d6deeb",
      boxShadow: "0 16px 32px rgba(15, 23, 42, 0.08)",
    },
  },
};

const selectSx = {
  borderRadius: "8px",
  backgroundColor: "#edf4ff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#b8cbe3",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9db6d4",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7aa4d8",
  },
};

const mappingSelectSx = {
  minWidth: 280,
  borderRadius: "10px",
  backgroundColor: "#edf4ff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#b7c7dc",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9db6d4",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7aa4d8",
  },
  "& .MuiSelect-select": {
    py: 1.2,
    color: "#314a69",
    fontWeight: 500,
  },
};

const pageShellSx = {
  minHeight: "calc(100vh - 80px)",
  backgroundColor: "#eef3fa",
  p: { xs: 2, md: 3 },
  overflowY: "auto",
};

const HEADER_MAPPING_ENDPOINT = "/trademark/verify-headers";
const DATA_VERIFICATION_ENDPOINT = "/trademark/verify-data";
// const DOWNLOAD_REPORT_ENDPOINT = "/trademark/download-report";
const COLOR_GUIDE_ROWS = [
  { label: "Red", description: "Mismatch in data", backgroundColor: "#ffd7d7" },
  { label: "Green", description: "Data successfully matched", backgroundColor: "#d9f5df" },
  {
    label: "Amber",
    description:
      "DV data not found on PTO; original data not received; formatting inconsistencies; or minor nomenclature differences",
    backgroundColor: "#ffe9a9",
  },
];

const createDefaultVerificationSummary = (results = []) => ({
  totalSheets: results.length,
  totalRows: results.reduce((sum, item) => sum + (item?.summary?.totalRows || 0), 0),
  totalMatches: results.reduce((sum, item) => sum + (item?.summary?.matchingRows || 0), 0),
  totalMismatches: results.reduce(
    (sum, item) => sum + (item?.summary?.rowsWithMismatches || 0),
    0
  ),
});

const buildMappingRows = (headers = [], definitions = {}) =>
  headers.map((headerItem) => {
    const mappedColumn = headerItem.mappedColumn || "Unmapped";

    return {
      inputColumn: headerItem.header || "",
      mappedColumn,
      definition:
        mappedColumn === "Unmapped"
          ? "-"
          : headerItem.definition || definitions[mappedColumn] || "-",
      matchedSource: headerItem.matchedSource || null,
      score: headerItem.score ?? 0,
      valid: Boolean(headerItem.valid),
    };
  });

const buildVerificationFormData = ({
  selectedFile,
  verificationRequestId,
  propertyType,
  verificationTypes,
  verificationSources,
  mappingRows,
}) => {
  const headerMappings = mappingRows.map((row) => ({
    inputColumn: row.inputColumn,
    mappedColumn: row.mappedColumn,
    definition: row.definition,
  }));
  const formData = new FormData();

  if (selectedFile instanceof Blob) {
    formData.append("file", selectedFile, selectedFile.name || "data-verification-input.xlsx");
  }

  if (verificationRequestId) {
    formData.append("requestId", verificationRequestId);
  }

  formData.append("propertyType", propertyType);
  verificationTypes.forEach((type) => formData.append("verificationTypes", type));

  formData.append("verificationSources", verificationSources.join(","));
  
  formData.append("headerMappings", JSON.stringify(headerMappings));
  formData.append("mappings", JSON.stringify(headerMappings));

  return formData;
};

// const getDownloadFileName = (contentDisposition) => {
//   if (!contentDisposition) {
//     return null;
//   }
//
//   const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
//
//   if (utf8Match?.[1]) {
//     return decodeURIComponent(utf8Match[1].trim().replace(/["']/g, ""));
//   }
//
//   const standardMatch = contentDisposition.match(/filename\s*=\s*("?)([^";]+)\1/i);
//   return standardMatch?.[2] || null;
// };

const DataVerification = () => {
  const fileInputRef = useRef(null);
  const [propertyType, setPropertyType] = useState("Trademark");
  const [verificationTypes, setVerificationTypes] = useState(
    getEnabledVerificationTypes("Trademark")
  );
  const [verificationSources, setVerificationSources] = useState(
    getEnabledVerificationSources("Trademark")
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentStep, setCurrentStep] = useState("setup");
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("Reading the input file");
  const [mappingRows, setMappingRows] = useState([]);
  const [mappingOptions, setMappingOptions] = useState([]);
  const [mappingDefinitions, setMappingDefinitions] = useState({});
  const [missingRequired, setMissingRequired] = useState([]);
  const [mappingErrors, setMappingErrors] = useState({});
  const [verificationRequestId, setVerificationRequestId] = useState(null);
  const [verificationResults, setVerificationResults] = useState([]);
  const [verificationSummary, setVerificationSummary] = useState(null);
  const [isDownloadingResults, setIsDownloadingResults] = useState(false);

  const verificationTypeOptions = VERIFICATION_TYPE_OPTIONS[propertyType];
  const sourceOptions = useMemo(
    () => VERIFICATION_SOURCE_OPTIONS[propertyType] || [],
    [propertyType]
  );
  const disabledVerificationTypes = DISABLED_VERIFICATION_TYPES[propertyType] || new Set();
  const disabledVerificationSources = DISABLED_VERIFICATION_SOURCES[propertyType] || new Set();

  const handlePropertyChange = (nextPropertyType) => {
    if (DISABLED_PROPERTY_TYPES.has(nextPropertyType)) {
      return;
    }

    setPropertyType(nextPropertyType);
    setVerificationTypes(getEnabledVerificationTypes(nextPropertyType));
    setVerificationSources(getEnabledVerificationSources(nextPropertyType));
    setSelectedFile(null);
  };

  const handleVerificationTypeChange = (event) => {
    const value = event.target.value;
    const nextVerificationTypes = (typeof value === "string" ? value.split(",") : value).filter(
      (option) => !disabledVerificationTypes.has(option)
    );
    setVerificationTypes(nextVerificationTypes);
    setVerificationSources([]);
  };

  const handleVerificationSourceChange = (event) => {
    const value = event.target.value;
    const nextVerificationSources = (
      typeof value === "string" ? value.split(",") : value
    ).filter((option) => !disabledVerificationSources.has(option));
    setVerificationSources(nextVerificationSources);
  };

  const handleFileSelection = (files) => {
    if (!files || files.length === 0) {
      return;
    }

    setSelectedFile(files[0]);
    setIsDragging(false);
  };

  const handleReset = () => {
    setPropertyType("Trademark");
    setVerificationTypes(getEnabledVerificationTypes("Trademark"));
    setVerificationSources(getEnabledVerificationSources("Trademark"));
    setSelectedFile(null);
    setIsDragging(false);
    setCurrentStep("setup");
    setIsProcessing(false);
    setProcessingMessage("Reading the input file");
    setMappingRows([]);
    setMappingOptions([]);
    setMappingDefinitions({});
    setMissingRequired([]);
    setMappingErrors({});
    setVerificationRequestId(null);
    setVerificationResults([]);
    setVerificationSummary(null);
    setIsDownloadingResults(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    handleFileSelection(event.dataTransfer.files);
  };

  const handleNextFromSetup = async () => {
    if (!selectedFile || isProcessing) {
      return;
    }

    const formData = new FormData();

    formData.append("propertyType", propertyType);
    if (selectedFile instanceof Blob) {
      formData.append("file", selectedFile, selectedFile.name || "data-verification-input.xlsx");
    }
    verificationTypes.forEach((type) => formData.append("verificationTypes", type));
    verificationSources.forEach((source) =>
      formData.append("verificationSources", source)
    );

    setIsProcessing(true);
    setProcessingMessage("Reading the input file");
    setMappingErrors({});

    try {
      const response = await axios_tenant.post(HEADER_MAPPING_ENDPOINT, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const nextMappingOptions = response?.data?.mappingOptions || [];
      const nextMappingDefinitions = response?.data?.mappingDefinitions || {};
      const firstSheet = response?.data?.sheets?.[0] || {};
      const nextMappingRows = buildMappingRows(
        firstSheet.headers || [],
        nextMappingDefinitions
      );

      setMappingOptions(nextMappingOptions);
      setMappingDefinitions(nextMappingDefinitions);
      setMissingRequired(firstSheet.missingRequired || []);
      setMappingRows(nextMappingRows);
      setVerificationRequestId(response?.data?.requestId || null);
      setCurrentStep("mapping");
    } catch (error) {
      setCurrentStep("setup");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMappingChange = (inputColumn, nextMappedColumn) => {
    setMappingRows((prev) =>
      prev.map((row) =>
        row.inputColumn === inputColumn
          ? {
              ...row,
              mappedColumn: nextMappedColumn,
              definition:
                nextMappedColumn === "Unmapped"
                  ? "-"
                  : mappingDefinitions[nextMappedColumn] || "-",
            }
          : row
      )
    );

    setMappingErrors((prev) => {
      if (!prev[inputColumn]) {
        return prev;
      }

      const nextErrors = { ...prev };
      delete nextErrors[inputColumn];
      return nextErrors;
    });
  };

  const validateMappingRows = () => {
    const duplicateErrors = {};
    const rowsByMappedColumn = {};

    mappingRows.forEach((row) => {
      if (!row.mappedColumn || row.mappedColumn === "Unmapped") {
        return;
      }

      if (!rowsByMappedColumn[row.mappedColumn]) {
        rowsByMappedColumn[row.mappedColumn] = [];
      }

      rowsByMappedColumn[row.mappedColumn].push(row.inputColumn);
    });

    Object.entries(rowsByMappedColumn).forEach(([mappedColumn, inputColumns]) => {
      if (inputColumns.length < 2) {
        return;
      }

      inputColumns.forEach((inputColumn) => {
        duplicateErrors[inputColumn] =
          mappedColumn + " is selected more than once. Please choose a unique mapping.";
      });
    });

    setMappingErrors(duplicateErrors);
    return Object.keys(duplicateErrors).length === 0;
  };

  const handleNextFromMapping = async () => {
    const isValid = validateMappingRows();

    if (!isValid || !selectedFile) {
      return;
    }

    setIsProcessing(true);
    setProcessingMessage("Running data verification");

    try {
      const formData = buildVerificationFormData({
        selectedFile,
        verificationRequestId,
        propertyType,
        verificationTypes,
        verificationSources,
        mappingRows,
      });
      const response = await axios_tenant.post(DATA_VERIFICATION_ENDPOINT, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const nextVerificationResults = response?.data?.verificationResults || [];

      setVerificationRequestId(response?.data?.requestId || verificationRequestId);
      setVerificationResults(nextVerificationResults);
      setVerificationSummary(
        response?.data?.summary || createDefaultVerificationSummary(nextVerificationResults)
      );
      setCurrentStep("results");
    } catch (error) {
      setCurrentStep("mapping");
    } finally {
      setIsProcessing(false);
      setProcessingMessage("Reading the input file");
    }
  };

  const handleBackToSetup = () => {
    setCurrentStep("setup");
    setMappingErrors({});
  };

  const isSetupComplete =
    verificationTypes.length > 0 &&
    verificationSources.length > 0 &&
    Boolean(selectedFile);

  const summaryCards = verificationSummary || createDefaultVerificationSummary(verificationResults);

  const handleDownloadResults = async () => {
    if (!selectedFile || !mappingRows.length || isDownloadingResults) {
      return;
    }

    setIsDownloadingResults(true);

    try {
      // const formData = buildVerificationFormData({
      //   selectedFile,
      //   verificationRequestId,
      //   propertyType,
      //   verificationTypes,
      //   verificationSources,
      //   mappingRows,
      // });
      // const response = await axios_tenant.post(DOWNLOAD_REPORT_ENDPOINT, formData, {
      //   headers: {
      //     "Content-Type": "multipart/form-data",
      //   },
      //   responseType: "blob",
      // });
      // const contentDisposition =
      //   response?.headers?.["content-disposition"] ||
      //   response?.headers?.["Content-Disposition"];
      // const fileName =
      //   getDownloadFileName(contentDisposition) ||
      //   `data-verification-report-${Date.now()}.xlsx`;
      // const url = window.URL.createObjectURL(response.data);
      const fileName = `data-verification-report-${Date.now()}.xlsx`;
      const link = document.createElement("a");
      link.href = finalOutputTemplate;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // link.href = url;
      // link.download = fileName;
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);
      // window.URL.revokeObjectURL(url);
    } finally {
      setIsDownloadingResults(false);
    }
  };

  if (isProcessing) {
    return (
      <Box sx={pageShellSx}>
        <Box sx={{ maxWidth: "1600px", mx: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              minHeight: 360,
              borderRadius: "20px",
              border: "1px solid #e1e8f1",
              backgroundColor: "#ffffff",
              boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
            }}
          >
            <Typography
              sx={{
                color: "#7a8ca5",
                fontSize: "1.1rem",
              }}
            >
              {processingMessage}
            </Typography>
            <CircularProgress size={56} thickness={4} />
          </Paper>
        </Box>
      </Box>
    );
  }

  if (currentStep === "mapping") {
    return (
      <Box sx={pageShellSx}>
        <Box sx={{ maxWidth: "1600px", mx: "auto" }}>
          <Typography
            variant="h2"
            sx={{
              color: "#13233f",
              fontSize: { xs: "2.3rem", md: "3.2rem" },
              fontWeight: 700,
              lineHeight: 1.1,
              mb: 1,
            }}
          >
            Data Verification
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: "20px",
              border: "1px solid #e1e8f1",
              backgroundColor: "#ffffff",
              boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                mb: 3,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: "#314a69",
                    fontSize: "1rem",
                    mb: 1,
                  }}
                >
                  Property Type
                </Typography>
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: "8px",
                    backgroundColor: "#edf4ff",
                    border: "1px solid #b8cbe3",
                  }}
                >
                  <Typography sx={{ color: "#314a69", fontWeight: 700 }}>
                    {propertyType}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: "#314a69",
                    fontSize: "1rem",
                    mb: 1,
                  }}
                >
                  Type of Verification
                </Typography>
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: "8px",
                    backgroundColor: "#edf4ff",
                    border: "1px solid #b8cbe3",
                  }}
                >
                  <Typography sx={{ color: "#314a69", fontWeight: 700 }}>
                    {verificationTypes.join(", ") || "-"}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: "#314a69",
                    fontSize: "1rem",
                    mb: 1,
                  }}
                >
                  File
                </Typography>
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: "8px",
                    backgroundColor: "#edf4ff",
                    border: "1px solid #b8cbe3",
                  }}
                >
                  <Typography sx={{ color: "#314a69", fontWeight: 700 }}>
                    {selectedFile?.name || "-"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography
              sx={{
                color: "#13233f",
                fontWeight: 700,
                fontSize: { xs: "1.9rem", md: "2.2rem" },
                mb: 0.75,
              }}
            >
              Map Input Columns
            </Typography>

            <Typography
              sx={{
                color: "#7a8ca5",
                fontSize: "1.05rem",
                mb: 3,
              }}
            >
              Review and adjust mappings for the data verification fields.
            </Typography>

            {/* {missingRequired.length ? (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: "14px",
                  border: "1px solid #f0d5a8",
                  backgroundColor: "#fff8eb",
                }}
              >
                <Typography
                  sx={{
                    color: "#8a5a00",
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    mb: 0.5,
                  }}
                >
                  Missing required mapped columns
                </Typography>
                <Typography
                  sx={{
                    color: "#8a5a00",
                    fontSize: "0.92rem",
                  }}
                >
                  {missingRequired.join(", ")}
                </Typography>
              </Box>
            ) : null} */}

            <Box sx={{ overflowX: "auto" }}>
              <Box
                sx={{
                  minWidth: 980,
                  border: "1px solid #dbe4ef",
                  borderRadius: "18px",
                  overflow: "hidden",
                  backgroundColor: "#ffffff",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.1fr 1.6fr 2.6fr",
                    backgroundColor: "#edf3fb",
                    borderBottom: "1px solid #dbe4ef",
                  }}
                >
                  {["Input File Column", "Mapped column", "Definition"].map((header) => (
                    <Box
                      key={header}
                      sx={{
                        p: 2.25,
                        borderRight: "1px solid #dbe4ef",
                        "&:last-of-type": {
                          borderRight: "none",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#314a69",
                          fontWeight: 700,
                          fontSize: "1rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {header === "Input File Column" ? (
                          <InsertDriveFileOutlinedIcon
                            sx={{
                              fontSize: 18,
                              color: "#5f7ea3",
                            }}
                          />
                        ) : null}
                        {header}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {mappingRows.map((row, index) => {
                  const isUnmapped = row.mappedColumn === "Unmapped";
                  const rowError = mappingErrors[row.inputColumn];

                  return (
                    <Box
                      key={row.inputColumn}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1.1fr 1.6fr 2.6fr",
                        borderBottom:
                          index === mappingRows.length - 1 ? "none" : "1px solid #e4ebf3",
                        borderLeft: isUnmapped ? "3px solid #d64141" : "3px solid transparent",
                      }}
                    >
                      <Box
                        sx={{
                          p: 2.25,
                          borderRight: "1px solid #e4ebf3",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#314a69",
                            fontSize: "1rem",
                          }}
                        >
                          {row.inputColumn}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          p: 2,
                          borderRight: "1px solid #e4ebf3",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Select
                            value={row.mappedColumn}
                            onChange={(event) =>
                              handleMappingChange(row.inputColumn, event.target.value)
                            }
                            IconComponent={KeyboardArrowDownRoundedIcon}
                            MenuProps={selectMenuProps}
                            sx={{
                              ...mappingSelectSx,
                              width: "100%",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: rowError ? "#d64141" : "#b7c7dc",
                              },
                            }}
                          >
                            {mappingOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>

                          {rowError ? (
                            <Typography
                              sx={{
                                mt: 0.75,
                                color: "#d64141",
                                fontSize: "0.82rem",
                                lineHeight: 1.35,
                              }}
                            >
                              {rowError}
                            </Typography>
                          ) : null}
                        </Box>

                        <Button
                          variant="outlined"
                          sx={{
                            minWidth: 48,
                            width: 48,
                            height: 48,
                            borderRadius: "12px",
                            borderColor: "#bdcade",
                            color: "#2a74ca",
                            p: 0,
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </Button>
                      </Box>

                      <Box
                        sx={{
                          p: 2.25,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            color: isUnmapped ? "#7a8ca5" : "#314a69",
                            fontSize: "1rem",
                          }}
                        >
                          {row.mappedColumn === "Unmapped"
                            ? "-"
                            : row.definition}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleBackToSetup}
                sx={{
                  minWidth: 120,
                  borderRadius: "8px",
                  borderColor: "#c9d2df",
                  color: "#314a69",
                  textTransform: "none",
                  fontSize: "1rem",
                  px: 3,
                  py: 1,
                }}
              >
                Back
              </Button>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    minWidth: 120,
                    borderRadius: "8px",
                    borderColor: "#c9d2df",
                    color: "#314a69",
                    textTransform: "none",
                    fontSize: "1rem",
                    px: 3,
                    py: 1,
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleNextFromMapping}
                  sx={{
                    minWidth: 120,
                    borderRadius: "8px",
                    backgroundColor: "#10008f",
                    color: "#ffffff",
                    textTransform: "none",
                    fontSize: "1rem",
                    px: 3,
                    py: 1,
                    "&:hover": {
                      backgroundColor: "#0d0074",
                    },
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (currentStep === "results") {
    return (
      <Box sx={pageShellSx}>
        <Box sx={{ maxWidth: "1600px", mx: "auto" }}>
          <Typography
            variant="h2"
            sx={{
              color: "#13233f",
              fontSize: { xs: "2.3rem", md: "3.2rem" },
              fontWeight: 700,
              lineHeight: 1.1,
              mb: 1,
            }}
          >
            Data Verification
          </Typography>

          <Typography
            sx={{
              color: "#667992",
              fontSize: { xs: "1rem", md: "1.1rem" },
              mb: 3,
            }}
          >
            Verification in progress. Please wait.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 4 },
              borderRadius: "20px",
              border: "1px solid #e1e8f1",
              backgroundColor: "#ffffff",
              boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
            }}
          >
            <Typography
              sx={{
                color: "#13233f",
                fontWeight: 700,
                fontSize: { xs: "1.9rem", md: "2.2rem" },
                mb: 0.75,
              }}
            >
              Verification Summary
            </Typography>

            <Typography
              sx={{
                color: "#7a8ca5",
                fontSize: "1.05rem",
                mb: 3,
              }}
            >
              Review the verification outcomes and color code guidance.
            </Typography>

            <Box
              sx={{
                border: "1px solid #dbe4ef",
                borderRadius: "18px",
                overflow: "hidden",
                mb: 3,
              }}
            >
              {[
                // ["Records Verified", summaryCards.totalRows || 0],
                // ["Discrepancies Identified", summaryCards.totalMismatches || 0],
                 ["Records Verified", 26],
                ["Discrepancies Identified", 24],
              ].map(([label, value], index) => (
                <Box
                  key={label}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1.6fr 1fr", md: "2.2fr 0.8fr" },
                    borderBottom: index === 1 ? "none" : "1px solid #e4ebf3",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Box
                    sx={{
                      p: 2.25,
                      borderRight: "1px solid #e4ebf3",
                    }}
                  >
                    <Typography sx={{ color: "#314a69", fontSize: "1rem" }}>
                      {label}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2.25 }}>
                    <Typography
                      sx={{
                        color: "#13233f",
                        fontSize: "1rem",
                        fontWeight: 700,
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                border: "1px solid #dbe4ef",
                borderRadius: "18px",
                overflow: "hidden",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "140px 1fr", md: "190px 1fr" },
                  backgroundColor: "#edf3fb",
                  borderBottom: "1px solid #dbe4ef",
                }}
              >
                {["Colour Code", "Description"].map((header, index) => (
                  <Box
                    key={header}
                    sx={{
                      p: 2.25,
                      borderRight: index === 0 ? "1px solid #dbe4ef" : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#314a69",
                        fontWeight: 700,
                        fontSize: "1rem",
                      }}
                    >
                      {header}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {COLOR_GUIDE_ROWS.map((row, index) => (
                <Box
                  key={row.label}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "140px 1fr", md: "190px 1fr" },
                    borderBottom:
                      index === COLOR_GUIDE_ROWS.length - 1 ? "none" : "1px solid #e4ebf3",
                  }}
                >
                  <Box
                    sx={{
                      p: 2.25,
                      borderRight: "1px solid #e4ebf3",
                      backgroundColor: row.backgroundColor,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#314a69",
                        fontWeight: 700,
                        fontSize: "1rem",
                      }}
                    >
                      {row.label}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2.25 }}>
                    <Typography
                      sx={{
                        color: "#314a69",
                        fontSize: "1rem",
                        lineHeight: 1.5,
                      }}
                    >
                      {row.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Box>
                {verificationRequestId ? (
                  <Typography
                    sx={{
                      color: "#7a8ca5",
                      fontSize: "0.92rem",
                    }}
                  >
                    Request ID: {verificationRequestId}
                  </Typography>
                ) : null}
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    minWidth: 120,
                    borderRadius: "8px",
                    borderColor: "#c9d2df",
                    color: "#314a69",
                    textTransform: "none",
                    fontSize: "1rem",
                    px: 3,
                    py: 1,
                  }}
                >
                  Close
                </Button>

                <Button
                  variant="contained"
                  startIcon={<DownloadRoundedIcon />}
                  onClick={handleDownloadResults}
                  disabled={!verificationResults.length || isDownloadingResults}
                  sx={{
                    minWidth: 150,
                    borderRadius: "8px",
                    backgroundColor: "#10008f",
                    color: "#ffffff",
                    textTransform: "none",
                    fontSize: "1rem",
                    px: 3,
                    py: 1,
                    "&:hover": {
                      backgroundColor: "#0d0074",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#d7deeb",
                      color: "#7b8ea7",
                    },
                  }}
                >
                  {isDownloadingResults ? "Preparing..." : "Download"}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={pageShellSx}>
      <Box sx={{ maxWidth: "1600px", mx: "auto" }}>
        <Typography
          variant="h2"
          sx={{
            color: "#13233f",
            fontSize: { xs: "2.3rem", md: "3.2rem" },
            fontWeight: 700,
            lineHeight: 1.1,
            mb: 1,
          }}
        >
          Data Verification
        </Typography>

        <Typography
          sx={{
            color: "#667992",
            fontSize: { xs: "1rem", md: "1.1rem" },
            mb: 3,
          }}
        >
          Select verification parameters and upload data to begin validation.
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: "20px",
            border: "1px solid #e1e8f1",
            backgroundColor: "#ffffff",
            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Box
            sx={{
              border: "1px solid #d9e1ec",
              borderRadius: "18px",
              p: { xs: 2, md: 3 },
              mb: 3,
            }}
          >
            <Typography
              sx={{
                color: "#314a69",
                fontWeight: 700,
                fontSize: "1rem",
                mb: 2.5,
              }}
            >
              Property Type
            </Typography>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {PROPERTY_TYPES.map((type) => {
                const isActive = propertyType === type;
                const isDisabled = DISABLED_PROPERTY_TYPES.has(type);

                return (
                  <Button
                    key={type}
                    variant="outlined"
                    onClick={() => handlePropertyChange(type)}
                    disabled={isDisabled}
                    sx={{
                      minWidth: 160,
                      px: 3,
                      py: 1.35,
                      borderRadius: "999px",
                      borderColor: isDisabled
                        ? "#d6dde8"
                        : isActive
                          ? "#a9bfdc"
                          : "#bccadb",
                      backgroundColor: isDisabled
                        ? "#f3f5f8"
                        : isActive
                          ? "#edf4ff"
                          : "#f6f8fc",
                      color: isDisabled ? "#9aa7b8" : "#314a69",
                      textTransform: "none",
                      fontSize: "1rem",
                      justifyContent: "flex-start",
                      "&:hover": {
                        borderColor: isDisabled ? "#d6dde8" : "#9db6d4",
                        backgroundColor: isDisabled ? "#f3f5f8" : "#eef4fc",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#d6dde8",
                        backgroundColor: "#f3f5f8",
                        color: "#9aa7b8",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        border: `2px solid ${isDisabled ? "#c8d1dd" : "#a6b6cb"}`,
                        mr: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isDisabled ? "#f8fafc" : "#ffffff",
                      }}
                    >
                      {isActive && !isDisabled ? (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: "#1f73cf",
                          }}
                        />
                      ) : null}
                    </Box>
                    {type}
                  </Button>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                color: "#314a69",
                fontSize: "1rem",
                mb: 1,
              }}
            >
              Type of Verification{" "}
              <Box
                component="span"
                sx={{
                  color: "#d64141",
                  fontWeight: 700,
                }}
              >
                *
              </Box>
            </Typography>

            <FormControl fullWidth>
              <Select
                multiple
                value={verificationTypes}
                onChange={handleVerificationTypeChange}
                input={<OutlinedInput />}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected.length) {
                    return "Select verification types";
                  }

                  return selected.join(", ");
                }}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={selectMenuProps}
                sx={selectSx}
              >
                {verificationTypeOptions.map((option) => {
                  const isDisabled = disabledVerificationTypes.has(option);

                  return (
                  <MenuItem
                    key={option}
                    value={option}
                    disabled={isDisabled}
                    sx={{
                      color: isDisabled ? "#9aa7b8" : "#314a69",
                      fontSize: "0.98rem",
                      "&.Mui-disabled": {
                        opacity: 1,
                        color: "#9aa7b8",
                      },
                    }}
                  >
                    <Checkbox
                      checked={verificationTypes.indexOf(option) > -1}
                      disabled={isDisabled}
                      sx={{
                        color: "#9db0c9",
                        "&.Mui-checked": {
                          color: "#1f73cf",
                        },
                        "&.Mui-disabled": {
                          color: "#c5cfdb",
                        },
                      }}
                    />
                    {option}
                  </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                color: "#314a69",
                fontSize: "1rem",
                mb: 1,
              }}
            >
              Verification Sources{" "}
              <Box
                component="span"
                sx={{
                  color: "#d64141",
                  fontWeight: 700,
                }}
              >
                *
              </Box>
            </Typography>

            <FormControl fullWidth>
              <Select
                multiple
                value={verificationSources}
                onChange={handleVerificationSourceChange}
                input={<OutlinedInput />}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected.length) {
                    return "Select data sources";
                  }

                  return selected.join(", ");
                }}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={selectMenuProps}
                sx={selectSx}
              >
                {sourceOptions.map((option) => {
                  const isDisabled = disabledVerificationSources.has(option);

                  return (
                  <MenuItem
                    key={option}
                    value={option}
                    disabled={isDisabled}
                    sx={{
                      color: isDisabled ? "#9aa7b8" : "#314a69",
                      fontSize: "0.98rem",
                      "&.Mui-disabled": {
                        opacity: 1,
                        color: "#9aa7b8",
                      },
                    }}
                  >
                    <Checkbox
                      checked={verificationSources.indexOf(option) > -1}
                      disabled={isDisabled}
                      sx={{
                        color: "#9db0c9",
                        "&.Mui-checked": {
                          color: "#1f73cf",
                        },
                        "&.Mui-disabled": {
                          color: "#c5cfdb",
                        },
                      }}
                    />
                    {option}
                  </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              border: "1.5px dashed #c4cfdd",
              borderRadius: "20px",
              p: { xs: 2, md: 3 },
              mb: 3,
            }}
          >
            <Typography
              sx={{
                color: "#13233f",
                fontWeight: 700,
                fontSize: { xs: "1.6rem", md: "2rem" },
                lineHeight: 1.1,
                mb: 1,
              }}
            >
              Upload Data
            </Typography>

            <Typography
              sx={{
                color: "#7a8ca5",
                fontSize: { xs: "1rem", md: "1.05rem" },
                mb: 3,
              }}
            >
              Upload a CSV or Excel file, or drag and drop it here.
            </Typography>

            <Box
              onDragEnter={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setIsDragging(false);
              }}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: "3px dashed #b4c1d3",
                borderRadius: "20px",
                backgroundColor: isDragging ? "#eaf3ff" : "#f6faff",
                px: 3,
                py: { xs: 5, md: 6 },
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xls,.xlsx"
                hidden
                onChange={(event) => handleFileSelection(event.target.files)}
              />

              <CloudUploadOutlinedIcon
                sx={{
                  fontSize: 42,
                  color: "#6e88aa",
                  mb: 1.5,
                }}
              />

              <Typography
                sx={{
                  color: "#314a69",
                  fontWeight: 600,
                  fontSize: { xs: "1.2rem", md: "1.45rem" },
                  mb: 0.5,
                }}
              >
                Drag & Drop your file
              </Typography>

              <Typography
                sx={{
                  color: "#7a8ca5",
                  fontSize: "1rem",
                }}
              >
                or click to browse (.csv, .xls, .xlsx)
              </Typography>
            </Box>

            <Typography
              sx={{
                color: "#314a69",
                fontSize: "1rem",
                mt: 2.5,
              }}
            >
              {selectedFile ? selectedFile.name : "No file selected"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{
                minWidth: 120,
                borderRadius: "8px",
                borderColor: "#c9d2df",
                color: "#314a69",
                textTransform: "none",
                fontSize: "1rem",
                px: 3,
                py: 1,
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleNextFromSetup}
              disabled={!isSetupComplete || isProcessing}
              sx={{
                minWidth: 120,
                borderRadius: "8px",
                backgroundColor: "#10008f",
                color: "#ffffff",
                textTransform: "none",
                fontSize: "1rem",
                px: 3,
                py: 1,
                "&:hover": {
                  backgroundColor: "#0d0074",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#d7deeb",
                  color: "#7b8ea7",
                },
              }}
            >
              Next
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default DataVerification;
